<template lang="html">
  <div class="page-section slideshow">
    <h2 class="list-title" v-if="title" v-html="title"></h2>

    <div>
      <Carousel3d
        ref="slideShow"
        :perspective="0"
        :display="1"
        :controlsVisible="true"
        :disable3d="true"
        :startIndex="currentSlideIndex"
        @before-slide-change="setSlideshowHeight"
        :height="slideshowHeight"
        unit="vh"
        :border="0"
      >
        <Slide
          v-for="(item, index) in items"
          :index="index"
          :class="['slideshow-slide', slideClasses(parse(item))]"
          unit="vh"
          :key="`carousel-item-${index}`"
        >
          <div
            class="slideshow-background"
            :style="backgroundImg(item)"
            :class="animationClass(item)">
          </div>

          <div class="slideshow-inner">
            <h1 v-if="parse(item).heading" class="heading" @click="highlightField('heading')">
              {{ parse(item).heading }}
            </h1>

            <h2 v-if="parse(item).sub_heading" class="heading" @click="highlightField('sub heading')">
              {{ parse(item).sub_heading }}
            </h2>

            <p v-if="parse(item).text" @click="highlightField('text')">{{ parse(item).text }}</p>

            <button
              v-if="parse(item).button_text"
              @click="highlightField('button link')"
              class="btn is-light"
              :title="`Will link to: ${parse(item).button_link}`"
            >
              {{ parse(item).button_text }}
            </button>
          </div>
        </Slide>
      </Carousel3d>
    </div>
  </div>
</template>

<script>
import parseComponent from '@/mixins/parseComponent'

import Carousel3d from './Carousel/Carousel3d'
import Slide from './Carousel/Slide'

export default {
  name: 'Slideshow',

  mixins: [parseComponent],

  props: {
    title: String,

    demoMode: Boolean,

    currentListItem: {
      type: Object,
      description: 'This is the type of list'
    },

    items: {
      type: Array,
      description: 'The current list item'
    }
  },

  data() {
    return {
      slideshowHeight: '0'
    }
  },

  components: {
    Carousel3d,
    Slide
  },

  computed: {
    slideClasses() {
      return (slide) => {
        let hasContent = slide.heading || slide.sub_heading || slide.text

        return [{'has-content': hasContent}]
      }
    },

    animationClass() {
      return (slide) => {
        return this.parse(slide).slide_animation ? `animate-${this.parse(slide).slide_animation}` : ''
      }
    },

    backgroundImg() {
      return (item) => {
        let image = this.demoMode ? this.parse(item).image_url : item.image_versions[0].url
        return `background-image: url('${image}');background-color:${this.parse(item).background_colour}`
      }
    },

    currentSlideIndex() {
      return this.items.indexOf(this.currentListItem)
    }
  },

  methods: {
    goToInitalSlide() {
      this.$refs.slideShow.goSlide(this.currentSlideIndex)
    },

    setSlideshowHeight(index) {
      // We're going to have a workaround as we don't want vh for the admin area
      let item = this.items[index]
      let newHeight

      switch (this.parse(item).slide_height) {
        case '100vh':
          newHeight = 65
          break
        case '50vh':
          newHeight = 32
          break
        case '33.333vh':
          newHeight = 22
          break
        case '66.666vh':
          newHeight = 44
          break
        case '25vh':
          newHeight = 18
          break
        default:
          newHeight = 65
      }

      this.slideshowHeight = newHeight
    }
  },

  mounted() {
    this.setSlideshowHeight(0)

    window.setTimeout(() => {
      this.goToInitalSlide()
    }, 1000)
  },

  watch: {
    'currentListItem.value'() {
      this.goToInitalSlide()
    }
  }
}
</script>

<style lang="sass">
@import '../../stylesheets/base/variables'
@import '../../stylesheets/helpers/mixins'

.slideshow
  width: 100%!important

  .carousel-3d-controls span
    color: #fff

  &.page-section
    padding: 0

  .slideshow-slide
    &.has-content
      &:after
        content: ''
        background: rgba(0, 0, 0, 0.3)
        position: absolute
        top: 0
        left: 0
        width: 100%
        height: 100%

    &.full-screen
      height: 100vh!important

    &.half-screen
      height: 50vh

  .slideshow-background,
  .slideshow-inner
    position: absolute
    top: 0
    left: 0
    width: 100%
    height: 100%
    background-size: cover
    display: flex
    align-items: center
    justify-content: center
    flex-direction: column
    text-align: center
    background-position: 50% 50%
    background-repeat: no-repeat

  .slideshow-inner
    z-index: 2

    h1, h2, h3
      margin: 10px 0
      line-height: 1
      color: #fff

    h1
      font-size: 56px

    h2
      font-size: 32px

    h3
      font-size: 24px

    p
      color: #fff
      font-weight: bold
      font-size: 18px
      margin-top: 0

    .btn
      color: #333!important
      padding: $gap-small $gap-medium

  .carousel-3d-container
    width: 100%
    margin: 0
    transition: all .3s ease-in-out

  .carousel-3d-slide, .carousel-3d-slider
    width: 100%!important

  .carousel-3d-slide
    transition: all .3s ease-in-out

    .slideshow-inner
      opacity: 0
      transition: all .5s 1s ease-in-out

    &.current
      .slideshow-inner
        opacity: 1
        transition: all .5s ease-in-out

  @keyframes zoom-in
    0%
      transform: scale(1)

    100%
      transform: scale(1.2)

  @keyframes zoom-out
    0%
      transform: scale(1.2)

    100%
      transform: scale(1)

  @keyframes pan-up
    0%
      transform: scale(1.2) translateY(-8%)

    100%
      transform: scale(1.2) translateY(0)

  @keyframes pan-down
    0%
      transform: scale(1.2) translateY(0)

    100%
      transform: scale(1.2) translateY(-8%)

  @keyframes pan-left
    0%
      transform: scale(1.2) translateX(-8%)

    100%
      transform: scale(1.2) translateX(0)

  @keyframes pan-right
    0%
      transform: scale(1.2) translateX(0)

    100%
      transform: scale(1.2) translateX(-8%)

  .current .animate-zoom-out
    animation: zoom-out 2.5s ease-in-out
    animation-fill-mode: forwards

  .current .animate-zoom-in
    animation: zoom-in 2.5s ease-in-out
    animation-fill-mode: forwards

  .current .animate-pan-up
    animation: pan-up 3.5s ease-in-out
    animation-fill-mode: forwards

  .current .animate-pan-down
    animation: pan-down 3.5s ease-in-out
    animation-fill-mode: forwards

  .current .animate-pan-left
    animation: pan-left 3.5s ease-in-out
    animation-fill-mode: forwards

  .current .animate-pan-right
    animation: pan-right 3.5s ease-in-out
    animation-fill-mode: forwards
</style>
